import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  LogOut, DollarSign, Clock, Package, TrendingUp, ChefHat, X, ChevronRight, MapPin, Phone,
  MessageCircle, ClipboardCheck, Copy, Printer, Bike, Navigation, User, Shield, Check, Bell, Utensils,
  ArrowLeft, Receipt, Percent, Search, Eye, EyeOff, Loader2,
} from "lucide-react";
import { playRestaurantDing, startCaribbeanAlert } from "@/lib/sounds";
import ContactDetailsModal from "@/components/ContactDetailsModal";

const COLUMNS = [
  { key: "placed", label: "New", tone: "bg-white/5 border-white/10" },
  { key: "preparing", label: "Preparing", tone: "bg-amber-500/10 border-amber-500/20" },
  { key: "ready", label: "Ready", tone: "bg-blue-500/10 border-blue-500/20" },
  { key: "picked_up", label: "Picked up", tone: "bg-purple-500/10 border-purple-500/20" },
  { key: "delivered", label: "Delivered", tone: "bg-eatup-green/10 border-eatup-green/20" },
];

const NEXT_STATUS = { placed: "preparing", preparing: "ready", ready: "picked_up", picked_up: "delivered" };
const STATUS_LABEL = { placed: "New", preparing: "Preparing", ready: "Ready", picked_up: "Picked up", delivered: "Delivered", cancelled: "Cancelled" };

// Threshold (in minutes) after which the auto-delay-check popup asks the
// restaurant if a still-in-kitchen order is running late.
const AUTO_DELAY_THRESHOLD_MIN = 20;
// Amount of minutes added to the customer's ETA when the manager confirms
// the auto-delay-check by clicking "Yes".
const AUTO_DELAY_ADD_MIN = 10;

export default function RestaurantDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);
  const [incomingOrder, setIncomingOrder] = useState(null);
  const [activeView, setActiveView] = useState("dashboard"); // dashboard | today_orders | pending | revenue
  const [delayCheckOrder, setDelayCheckOrder] = useState(null); // single-order auto-prompt
  const [batchDelay, setBatchDelay] = useState(null); // { orders: [...] } when ≥2 overdue at once
  const prevPlacedIdsRef = useRef(new Set());
  const dismissedIncomingRef = useRef(new Set());
  const autoDelayCheckedRef = useRef(new Set()); // order_ids we've already prompted (or the user answered "No" to)
  const soundEnabledRef = useRef(false);

  useEffect(() => {
    const enable = () => { soundEnabledRef.current = true; window.removeEventListener("pointerdown", enable); };
    window.addEventListener("pointerdown", enable, { once: true });
    return () => window.removeEventListener("pointerdown", enable);
  }, []);

  const load = async () => {
    try {
      const [o, r] = await Promise.all([
        api.get("/orders/mine").then((x) => x.data),
        api.get("/restaurants").then((x) => x.data),
      ]);
      const placedIds = o.filter((x) => x.status === "placed").map((x) => x.order_id);
      const prev = prevPlacedIdsRef.current;
      const fresh = placedIds.filter((id) => !prev.has(id));
      if (fresh.length && prev.size > 0 && soundEnabledRef.current) {
        playRestaurantDing();
        toast.success(`New order${fresh.length > 1 ? "s" : ""} received!`);
      }
      prevPlacedIdsRef.current = new Set(placedIds);

      // Auto-popup incoming order (full-screen notification, matches driver flow).
      // No sound-gate check — the modal itself is silent-safe; audio starts inside the
      // modal after any user gesture on the dashboard.
      // Suppress while manager is editing the menu — a new order shouldn't
      // hijack their menu workflow.
      if (!incomingOrder && !detailOrder && activeView !== "menu") {
        const next = o.find(
          (x) => x.status === "placed" && !dismissedIncomingRef.current.has(x.order_id),
        );
        if (next) setIncomingOrder(next);
      }

      setOrders(o);
      setRestaurants(r);
      if (!selected && r.length) setSelected(r[0].restaurant_id);
      if (detailOrder) {
        const fresh2 = o.find((x) => x.order_id === detailOrder.order_id);
        if (fresh2) setDetailOrder(fresh2);
      }
      if (incomingOrder) {
        const fresh3 = o.find((x) => x.order_id === incomingOrder.order_id);
        // Auto-dismiss modal if order is no longer 'placed' (e.g. accepted elsewhere)
        if (!fresh3 || fresh3.status !== "placed") setIncomingOrder(null);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => selected ? orders.filter((o) => o.restaurant_id === selected) : orders, [orders, selected]);

  // Orders handed off to a driver today (pickup PIN verified). Once verified
  // the kitchen's job is done — these no longer belong in the active queue.
  const handedOffToday = useMemo(() => {
    const today = new Date().toDateString();
    return filtered
      .filter((o) => new Date(o.created_at).toDateString() === today)
      .filter((o) => o.pickup_verified || ["picked_up", "on_the_way", "delivered"].includes(o.status))
      .sort((a, b) => {
        const at = new Date(a.pickup_verified_at || a.created_at);
        const bt = new Date(b.pickup_verified_at || b.created_at);
        return bt - at;
      });
  }, [filtered]);

  const stats = useMemo(() => {
    const today = new Date().toDateString();
    const todayOrders = filtered.filter((o) => new Date(o.created_at).toDateString() === today);
    const revenue = todayOrders.reduce((s, o) => s + (o.total || 0), 0);
    // "Pending" now means orders that still need kitchen action — verified
    // pick-ups are the driver's problem, so they drop out of the count.
    const pending = filtered.filter(
      (o) => ["placed", "preparing", "ready"].includes(o.status) && !o.pickup_verified,
    ).length;
    return {
      revenue: Math.round(revenue * 100) / 100,
      todayCount: todayOrders.length,
      pending,
      handedOffToday: handedOffToday.length,
      lifetime: filtered.length,
    };
  }, [filtered, handedOffToday]);

  const setStatus = async (orderId, status) => {
    try {
      await api.put(`/orders/${orderId}/status`, { status });
      toast.success(`Marked as ${STATUS_LABEL[status] || status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reportDelay = async (orderId, minutes, opts = {}) => {
    try {
      const { data } = await api.post(`/orders/${orderId}/delay`, {
        minutes,
        reason: opts.reason || "",
        auto: !!opts.auto,
      });
      toast.success(`Delay reported: +${minutes} min · new ETA ${data.eta_minutes} min`);
      load();
      return true;
    } catch (e) { toast.error(formatApiError(e)); return false; }
  };

  const reportBatchDelay = async (orderIds, minutes) => {
    try {
      const { data } = await api.post(`/orders/delay-batch`, {
        order_ids: orderIds,
        minutes,
        reason: "Auto-check acknowledged in batch",
        auto: true,
      });
      const skippedSuffix = data.skipped_count
        ? ` (${data.skipped_count} skipped — already out for delivery)`
        : "";
      toast.success(
        `+${minutes} min applied to ${data.applied_count} order${data.applied_count === 1 ? "" : "s"}${skippedSuffix} · drivers & customers notified`,
      );
      load();
      return true;
    } catch (e) { toast.error(formatApiError(e)); return false; }
  };

  // Automated Delay Check — scan every 30s for orders that have been in the
  // kitchen for AUTO_DELAY_THRESHOLD_MIN or longer without hitting "ready".
  // Single overdue → the one-at-a-time modal. ≥2 overdue → the batch "Acknowledge
  // all" modal so managers can bump every stalled order in one shot.
  useEffect(() => {
    const thresholdMs = AUTO_DELAY_THRESHOLD_MIN * 60 * 1000;
    const check = () => {
      if (delayCheckOrder || batchDelay || incomingOrder) return; // don't stack modals
      if (activeView === "menu") return; // don't interrupt menu editing
      const now = Date.now();
      const candidates = orders.filter((o) => {
        if (!["placed", "preparing"].includes(o.status)) return false;
        if (autoDelayCheckedRef.current.has(o.order_id)) return false;
        const ageMs = now - new Date(o.created_at).getTime();
        return ageMs >= thresholdMs;
      });
      if (candidates.length === 0) return;
      if (candidates.length === 1) {
        setDelayCheckOrder(candidates[0]);
      } else {
        setBatchDelay({ orders: candidates });
      }
    };
    check();
    const t = setInterval(check, 30000);
    return () => clearInterval(t);
  }, [orders, delayCheckOrder, batchDelay, incomingOrder, activeView]);

  return (
    <div className="min-h-screen bg-eatup-dark-bg text-white">
      <header className="sticky top-0 z-40 glass-dark">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3.5 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <img src="/eats-gnd-logo.png" alt="Eats GND" className="w-10 h-10 rounded-xl object-cover shadow-md ring-1 ring-white/10" draggable={false} />
            <span className="font-display text-xl font-bold text-white">Eats GND <span className="text-eatup-green">Restaurant</span></span>
          </div>
          <select
            data-testid="restaurant-select"
            value={selected || ""}
            onChange={(e) => setSelected(e.target.value)}
            className="ml-4 hidden md:block px-3 py-2 rounded-full bg-white/10 border border-white/10 text-white text-sm font-semibold outline-none focus:border-eatup-green"
          >
            {restaurants.map((r) => <option key={r.restaurant_id} value={r.restaurant_id} className="bg-eatup-dark-surface">{r.name}</option>)}
          </select>
          <div className="flex-1" />
          <button
            data-testid="btn-manage-menu"
            onClick={() => setActiveView(activeView === "menu" ? "dashboard" : "menu")}
            className={`hidden sm:inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition ${
              activeView === "menu"
                ? "bg-eatup-green text-black"
                : "bg-white/10 text-white hover:bg-white/20"
            }`}
          >
            <Utensils className="w-3.5 h-3.5" /> {activeView === "menu" ? "Back to orders" : "Manage menu"}
          </button>
          <div className="hidden sm:block text-sm text-neutral-300">Signed in as <span className="font-semibold text-white">{user?.name}</span></div>
          <button onClick={async () => { await logout(); navigate("/login"); }} className="p-2.5 rounded-full bg-white/10 hover:bg-white/20 transition" data-testid="restaurant-logout-btn">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <select
          value={selected || ""}
          onChange={(e) => setSelected(e.target.value)}
          className="md:hidden mb-4 w-full px-4 py-3 rounded-2xl bg-white/10 border border-white/10 text-white outline-none"
        >
          {restaurants.map((r) => <option key={r.restaurant_id} value={r.restaurant_id} className="bg-eatup-dark-surface">{r.name}</option>)}
        </select>

        {activeView === "menu" ? (
          <MenuManager
            restaurantId={selected}
            restaurantName={restaurants.find((r) => r.restaurant_id === selected)?.name || ""}
            onBack={() => setActiveView("dashboard")}
          />
        ) : (
        <>
        <div className="grid gap-4 md:grid-cols-4">
          <Stat
            testid="stat-revenue"
            icon={DollarSign}
            label="Today's revenue"
            value={`$${stats.revenue.toFixed(2)}`}
            onClick={() => setActiveView("revenue")}
          />
          <Stat
            testid="stat-today-orders"
            icon={Package}
            label="Today's orders"
            value={stats.todayCount}
            onClick={() => setActiveView("today_orders")}
          />
          <Stat
            testid="stat-pending"
            icon={Clock}
            label="Pending"
            value={stats.pending}
            onClick={() => setActiveView("pending")}
          />
          <Stat
            testid="stat-completed"
            icon={Check}
            label="Completed today"
            value={stats.handedOffToday}
            onClick={() => setActiveView("completed")}
          />
        </div>

        {activeView !== "dashboard" && (
          <DrilldownView
            view={activeView}
            filtered={filtered}
            handedOffToday={handedOffToday}
            onBack={() => setActiveView("dashboard")}
            onOpenOrder={(o) => setDetailOrder(o)}
            onReportDelay={reportDelay}
          />
        )}

        {activeView === "dashboard" && (
          <>
        <PinQuickSearch
          orders={filtered}
          onVerify={async (orderId, code) => {
            try {
              await api.post(`/orders/${orderId}/verify-pickup`, { code });
              toast.success("Code verified — order released to driver");
              // Burst-refresh so the driver's pickup-photo (advances status → on_the_way)
              // lands on the restaurant view within a second or two.
              load();
              [1200, 2500, 4500, 7000].forEach((ms) => setTimeout(load, ms));
              return true;
            } catch (e) {
              toast.error(formatApiError(e));
              return false;
            }
          }}
          onOpenOrder={(o) => setDetailOrder(o)}
        />

        <DriverHandoffPanel
          orders={filtered}
          onVerify={async (orderId, code) => {
            try {
              await api.post(`/orders/${orderId}/verify-pickup`, { code });
              toast.success("Code verified — order released to driver");
              load();
              [1200, 2500, 4500, 7000].forEach((ms) => setTimeout(load, ms));
              return true;
            } catch (e) {
              toast.error(formatApiError(e));
              return false;
            }
          }}
        />

        <div className="mt-10">
          <OrdersTable
            orders={filtered}
            onOpenOrder={(o) => setDetailOrder(o)}
            onAdvance={(o) => NEXT_STATUS[o.status] && setStatus(o.order_id, NEXT_STATUS[o.status])}
          />
        </div>

        {selected && <MenuPreview restaurantId={selected} />}
          </>
        )}
        </>
        )}
      </main>

      {incomingOrder && (
        <IncomingOrderModal
          order={incomingOrder}
          onAccept={async () => {
            try {
              await api.put(`/orders/${incomingOrder.order_id}/status`, { status: "preparing" });
              // Wait for the fresh list before closing the modal so the
              // dashboard state is guaranteed to reflect the new status.
              await load();
              toast.success("Order accepted — get cooking!");
              setIncomingOrder(null);
            } catch (e) {
              toast.error(formatApiError(e));
              throw e; // bubble so the modal can re-enable the button
            }
          }}
          onReject={async () => {
            dismissedIncomingRef.current.add(incomingOrder.order_id);
            try {
              await api.put(`/orders/${incomingOrder.order_id}/status`, { status: "cancelled" });
              await load();
              toast("Order rejected");
              setIncomingOrder(null);
            } catch (e) {
              toast.error(formatApiError(e));
              throw e;
            }
          }}
          onDismiss={() => {
            dismissedIncomingRef.current.add(incomingOrder.order_id);
            setIncomingOrder(null);
          }}
        />
      )}

      {detailOrder && (
        <RestaurantOrderDetails
          order={detailOrder}
          onClose={() => setDetailOrder(null)}
          onSetStatus={(s) => setStatus(detailOrder.order_id, s)}
          onReportDelay={(mins) => reportDelay(detailOrder.order_id, mins)}
          onVerifyPickup={async (code) => {
            try {
              await api.post(`/orders/${detailOrder.order_id}/verify-pickup`, { code });
              toast.success("Driver verified — order released!");
              load();
            } catch (e) { toast.error(formatApiError(e)); throw e; }
          }}
        />
      )}

      {delayCheckOrder && (
        <AutoDelayCheckModal
          order={delayCheckOrder}
          minutesLate={Math.floor((Date.now() - new Date(delayCheckOrder.created_at).getTime()) / 60000)}
          addMinutes={AUTO_DELAY_ADD_MIN}
          onYes={async () => {
            const ok = await reportDelay(delayCheckOrder.order_id, AUTO_DELAY_ADD_MIN, {
              auto: true,
              reason: "Auto-check confirmed by restaurant",
            });
            autoDelayCheckedRef.current.add(delayCheckOrder.order_id);
            setDelayCheckOrder(null);
            return ok;
          }}
          onNo={() => {
            autoDelayCheckedRef.current.add(delayCheckOrder.order_id);
            setDelayCheckOrder(null);
          }}
        />
      )}

      {batchDelay && (
        <BatchDelayModal
          orders={batchDelay.orders}
          onAcknowledgeAll={async (minutes) => {
            const ok = await reportBatchDelay(
              batchDelay.orders.map((o) => o.order_id),
              minutes,
            );
            batchDelay.orders.forEach((o) => autoDelayCheckedRef.current.add(o.order_id));
            setBatchDelay(null);
            return ok;
          }}
          onDismiss={() => {
            batchDelay.orders.forEach((o) => autoDelayCheckedRef.current.add(o.order_id));
            setBatchDelay(null);
          }}
        />
      )}
    </div>
  );
}

function OrdersTable({ orders, onOpenOrder, onAdvance }) {
  const [query, setQuery] = useState("");
  const [showDelivered, setShowDelivered] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // Tick every 1s so MM:SS updates live for pending-order timers.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders
      .filter((o) => {
        if (!showDelivered && ["delivered", "cancelled"].includes(o.status)) return false;
        if (!q) return true;
        const pin = o.pickup_code ? String(o.pickup_code).padStart(4, "0") : "";
        return (
          o.order_id.toLowerCase().endsWith(q) ||
          o.order_id.toLowerCase().includes(q) ||
          (o.customer_name || "").toLowerCase().includes(q) ||
          pin.includes(q.replace(/\D/g, ""))
        );
      })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [orders, query, showDelivered]);

  const activeCount = orders.filter((o) => !["delivered", "cancelled"].includes(o.status)).length;
  const doneCount = orders.length - activeCount;

  return (
    <section data-testid="orders-table" className="mt-2">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold text-white">Deliveries</h2>
          <div className="text-xs text-neutral-400">
            {activeCount} active · {doneCount} completed today
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
          Newest first
        </div>
      </div>

      {/* Controls */}
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none" />
          <input
            data-testid="orders-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by order #, customer, or driver PIN…"
            className="w-full pl-11 pr-9 py-2.5 rounded-full bg-white/5 border border-white/10 focus:border-eatup-green focus:bg-white/10 outline-none text-sm text-white placeholder:text-neutral-500"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              data-testid="orders-search-clear"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 text-neutral-300 flex items-center justify-center"
              aria-label="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button
          onClick={() => setShowDelivered((v) => !v)}
          data-testid="toggle-show-delivered"
          className={`flex items-center gap-2 text-xs font-semibold px-4 py-2.5 rounded-full border transition ${
            showDelivered
              ? "bg-eatup-green/20 border-eatup-green/40 text-eatup-green"
              : "bg-white/5 border-white/10 text-neutral-300 hover:border-white/25"
          }`}
        >
          {showDelivered ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          {showDelivered ? "Delivered visible" : "Hide delivered"}
        </button>
      </div>

      {/* Table */}
      <div className="mt-4 bg-eatup-dark-surface border border-white/10 rounded-3xl overflow-hidden">
        <div className="hidden md:grid grid-cols-[110px_1fr_120px_100px_auto] gap-3 px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-widest text-neutral-400 font-bold">
          <div>Order #</div>
          <div>Customer</div>
          <div>Status</div>
          <div>Timer</div>
          <div className="text-right">Action</div>
        </div>
        {rows.length === 0 ? (
          <div className="p-10 text-center text-neutral-400 text-sm" data-testid="orders-table-empty">
            {query ? `No orders match "${query}"` : showDelivered ? "No orders yet." : "No active orders. Toggle 'Delivered visible' to see completed."}
          </div>
        ) : (
          rows.map((o) => {
            const nextStatus = NEXT_STATUS[o.status];
            return (
              <div
                key={o.order_id}
                data-testid={`orders-row-${o.order_id}`}
                className="grid grid-cols-[110px_1fr_auto] md:grid-cols-[110px_1fr_120px_100px_auto] gap-3 items-center px-5 py-3 border-b border-white/5 last:border-b-0 hover:bg-white/5 transition"
              >
                <div className="font-mono text-eatup-green font-bold text-xs tabular">
                  #{o.order_id.slice(-6).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{o.customer_name}</div>
                  <div className="text-[11px] text-neutral-500 truncate md:hidden">
                    <DarkStatusBadge status={o.status} /> · <TimerCell createdAt={o.created_at} now={now} status={o.status} inline />
                  </div>
                  <div className="hidden md:block text-[11px] text-neutral-500 truncate">
                    {o.items.length} item{o.items.length !== 1 ? "s" : ""} · ${o.total.toFixed(2)}
                  </div>
                </div>
                <div className="hidden md:block">
                  <DarkStatusBadge status={o.status} />
                </div>
                <div className="hidden md:block">
                  <TimerCell createdAt={o.created_at} now={now} status={o.status} />
                </div>
                <div className="flex items-center gap-2 justify-end">
                  {nextStatus && nextStatus !== "delivered" && (
                    (() => {
                      const needsDriver = nextStatus === "picked_up" && !o.driver_id;
                      return (
                        <button
                          data-testid={`orders-advance-${o.order_id}`}
                          onClick={() => !needsDriver && onAdvance(o)}
                          disabled={needsDriver}
                          title={needsDriver ? "Waiting for a driver — verify their PIN once they arrive." : ""}
                          className={`hidden sm:inline-flex text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full transition ${
                            needsDriver
                              ? "bg-white/5 text-neutral-500 cursor-not-allowed"
                              : "bg-eatup-green/15 text-eatup-green hover:bg-eatup-green/25"
                          }`}
                        >
                          {needsDriver ? "Awaiting driver" : `→ ${STATUS_LABEL[nextStatus]}`}
                        </button>
                      );
                    })()
                  )}
                  <button
                    data-testid={`orders-view-${o.order_id}`}
                    onClick={() => onOpenOrder(o)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white text-eatup-charcoal hover:bg-neutral-100 transition flex items-center gap-1"
                  >
                    View <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}

function TimerCell({ createdAt, now, inline, status }) {
  const elapsedSec = Math.max(0, Math.floor((now - new Date(createdAt).getTime()) / 1000));
  const mins = Math.floor(elapsedSec / 60);
  const secs = elapsedSec % 60;
  const label = `${mins}:${String(secs).padStart(2, "0")}`;

  // Colour logic:
  // - "placed" (still awaiting restaurant acceptance): green until 10m, red after.
  // - anything downstream: existing warm→hot ramp so the kitchen sees stalled orders.
  let tone;
  if (status === "placed") {
    tone = mins >= 10 ? "text-red-400 animate-pulse" : "text-eatup-green";
  } else {
    tone = mins >= 25 ? "text-red-400" : mins >= 15 ? "text-eatup-yellow" : "text-neutral-300";
  }

  if (inline) return <span className={`tabular font-semibold ${tone}`}>{label}</span>;
  return (
    <div className="flex items-center gap-1.5" data-testid={`wait-timer-${status || "unknown"}`}>
      <Clock className={`w-3.5 h-3.5 ${tone}`} />
      <span className={`text-sm tabular font-semibold ${tone}`}>{label}</span>
    </div>
  );
}

function PinQuickSearch({ orders, onVerify, onOpenOrder }) {
  // High-contrast, thumb-friendly search bar that turns the driver's 4-digit
  // pickup PIN into an instant order match. When the 4 digits map to an active
  // order, we show a match card with one-tap "Verify Pickup" (same endpoint
  // used by the legacy handoff panel).
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  // Only orders that are still awaiting the restaurant's handoff release.
  const searchable = useMemo(
    () => orders.filter((o) =>
      ["placed", "preparing", "ready"].includes(o.status) &&
      o.driver_id &&
      !o.pickup_verified &&
      o.pickup_code != null
    ),
    [orders],
  );

  // Case-normalised, digit-only lookup so staff can type quickly.
  const normalized = code.replace(/\D/g, "").slice(0, 4);
  const match = normalized.length === 4
    ? searchable.find((o) => String(o.pickup_code).padStart(4, "0") === normalized)
    : null;
  const notFound = normalized.length === 4 && !match;

  const verify = async () => {
    if (!match || busy) return;
    setBusy(true);
    const ok = await onVerify(match.order_id, normalized);
    setBusy(false);
    if (ok) {
      setCode("");
      inputRef.current?.focus();
    }
  };

  return (
    <section
      className="mb-6 rounded-3xl bg-gradient-to-br from-eatup-yellow/20 via-eatup-yellow/10 to-transparent border-2 border-eatup-yellow/40 p-5 sm:p-6"
      data-testid="pin-quick-search"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-2xl bg-eatup-yellow text-eatup-charcoal flex items-center justify-center shrink-0">
          <Shield className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] font-black text-eatup-yellow">Quick Search · Driver Code</div>
          <h2 className="font-display font-bold text-lg sm:text-xl text-white leading-tight">
            Ask the driver for their 4-digit code
          </h2>
        </div>
        <div className="hidden sm:block text-[11px] text-neutral-400 font-semibold text-right max-w-[220px]">
          {searchable.length} driver{searchable.length !== 1 ? "s" : ""} waiting for handoff
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto] items-center">
        <div className="relative">
          <Shield className="w-5 h-5 absolute left-5 top-1/2 -translate-y-1/2 text-eatup-yellow/70 pointer-events-none" />
          <input
            ref={inputRef}
            data-testid="pin-quick-search-input"
            value={normalized}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && match) verify(); }}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            maxLength={4}
            placeholder="Enter 4-digit code"
            className={`w-full pl-14 pr-14 py-4 sm:py-5 rounded-2xl bg-eatup-charcoal border-2 outline-none font-display font-black text-3xl sm:text-4xl tabular tracking-[0.4em] text-eatup-yellow placeholder:text-neutral-600 placeholder:tracking-normal placeholder:font-normal placeholder:text-base transition ${
              match
                ? "border-eatup-green shadow-[0_0_0_6px_rgba(255,106,26,0.15)]"
                : notFound
                  ? "border-red-500/60"
                  : "border-eatup-yellow/30 focus:border-eatup-yellow"
            }`}
          />
          {normalized && (
            <button
              onClick={() => { setCode(""); inputRef.current?.focus(); }}
              data-testid="pin-quick-search-clear"
              className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-neutral-300 flex items-center justify-center"
              aria-label="Clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <button
          onClick={verify}
          disabled={!match || busy}
          data-testid="pin-quick-search-verify"
          className={`w-full sm:w-auto sm:min-w-[180px] py-4 sm:py-5 rounded-2xl font-display font-black uppercase tracking-widest text-sm sm:text-base transition ${
            match && !busy
              ? "bg-eatup-green hover:bg-eatup-green-dark text-white shadow-lg active:scale-[0.98]"
              : "bg-white/10 text-neutral-500 cursor-not-allowed"
          }`}
        >
          {busy ? "Verifying…" : match ? "Verify Pickup" : "Verify"}
        </button>
      </div>

      {/* Match card */}
      {match && (
        <button
          onClick={() => onOpenOrder(match)}
          data-testid={`pin-match-card-${match.order_id}`}
          className="mt-4 w-full text-left rounded-2xl bg-eatup-dark-surface border-2 border-eatup-green/50 p-4 hover:border-eatup-green hover:bg-eatup-green/5 transition active:scale-[0.995] flex items-center gap-4 fade-up"
        >
          <div className="w-12 h-12 rounded-xl bg-eatup-green/20 text-eatup-green flex items-center justify-center shrink-0">
            <Check className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-black text-eatup-green">Match found — release when ready</div>
            <div className="font-display font-bold text-white truncate">
              #{match.order_id.slice(-6).toUpperCase()} · {match.customer_name}
            </div>
            <div className="text-xs text-neutral-400 truncate mt-0.5">
              {match.items.length} item{match.items.length !== 1 ? "s" : ""} · ${match.total.toFixed(2)} · Driver: {match.driver?.name || "assigned"}
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-eatup-green shrink-0" />
        </button>
      )}

      {notFound && (
        <div
          data-testid="pin-not-found"
          className="mt-4 rounded-2xl border-2 border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200 font-semibold flex items-center gap-2"
        >
          <X className="w-4 h-4 shrink-0" />
          No active order matches PIN <span className="font-black tabular tracking-widest">{normalized}</span>. Ask the driver to confirm.
        </div>
      )}

      {!normalized && searchable.length > 0 && (
        <div className="mt-4 flex items-center gap-2 flex-wrap text-[11px] text-neutral-400">
          <span className="font-semibold uppercase tracking-widest">Waiting drivers:</span>
          {searchable.slice(0, 4).map((o) => (
            <span
              key={o.order_id}
              className="inline-flex items-center gap-1 rounded-full bg-white/5 border border-white/10 px-2.5 py-1 tabular"
            >
              <Shield className="w-3 h-3 text-eatup-yellow" />
              #{o.order_id.slice(-6).toUpperCase()} · {o.driver?.name?.split(" ")[0] || "driver"}
            </span>
          ))}
          {searchable.length > 4 && <span className="text-neutral-500">+{searchable.length - 4} more</span>}
        </div>
      )}
    </section>
  );
}

function DriverHandoffPanel({ orders, onVerify }) {
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(false);
  const inputsRef = useRef([]);

  // Orders with a driver assigned and still in kitchen stages (i.e. awaiting handoff).
  const readyForHandoff = useMemo(
    () => orders.filter((o) => o.driver_id && ["placed", "preparing", "ready"].includes(o.status)),
    [orders],
  );

  const focus = (idx) => { inputsRef.current[idx]?.focus(); };
  const setDigit = (idx, val) => {
    const v = (val || "").replace(/\D/g, "").slice(-1);
    setDigits((prev) => { const next = [...prev]; next[idx] = v; return next; });
    if (v && idx < 3) focus(idx + 1);
  };
  const onKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) focus(idx - 1);
    else if (e.key === "ArrowLeft" && idx > 0) focus(idx - 1);
    else if (e.key === "ArrowRight" && idx < 3) focus(idx + 1);
    else if (e.key === "Enter") submit();
  };
  const onPaste = (e) => {
    const text = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 4);
    if (!text) return;
    e.preventDefault();
    const arr = ["", "", "", ""];
    text.split("").forEach((c, i) => { arr[i] = c; });
    setDigits(arr);
    focus(Math.min(3, text.length));
  };

  const submit = async () => {
    const code = digits.join("");
    if (code.length !== 4) return;
    // Find the order whose pickup_code matches (only among orders awaiting handoff).
    const match = readyForHandoff.find((o) => String(o.pickup_code || "") === code);
    if (!match) {
      setShake(true);
      setTimeout(() => setShake(false), 400);
      toast.error("Wrong code — ask the driver to show their pick-up PIN.");
      setDigits(["", "", "", ""]);
      focus(0);
      return;
    }
    setBusy(true);
    const ok = await onVerify(match.order_id, code);
    setBusy(false);
    if (ok) setDigits(["", "", "", ""]);
    else { setShake(true); setTimeout(() => setShake(false), 400); }
  };

  const codeStr = digits.join("");

  return (
    <section
      data-testid="driver-handoff-panel"
      className="mt-8 rounded-3xl border border-eatup-yellow/30 bg-gradient-to-br from-eatup-yellow/10 via-eatup-charcoal/50 to-transparent p-5 sm:p-6"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-11 h-11 rounded-2xl bg-eatup-yellow/20 text-eatup-yellow flex items-center justify-center shrink-0">
          <Shield className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] uppercase tracking-[0.3em] text-eatup-yellow font-bold">Driver arrived?</div>
          <h3 className="mt-1 font-display text-xl font-bold text-white">Verify the pick-up code</h3>
          <p className="mt-1 text-sm text-neutral-300">
            Ask the driver to show their <span className="font-semibold text-eatup-yellow">4-digit pick-up PIN</span>.
            Enter it below to release the order — the driver's camera opens automatically once it's verified.
          </p>

          <form
            className={`mt-4 flex flex-col sm:flex-row items-center gap-3 ${shake ? "animate-[shake_0.4s_ease-in-out]" : ""}`}
            onSubmit={(e) => { e.preventDefault(); submit(); }}
          >
            <div className="flex items-center gap-2 sm:gap-3">
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (inputsRef.current[i] = el)}
                  data-testid={`handoff-pin-${i}`}
                  value={d}
                  onChange={(e) => setDigit(i, e.target.value)}
                  onKeyDown={(e) => onKeyDown(i, e)}
                  onPaste={onPaste}
                  onFocus={(e) => e.target.select()}
                  inputMode="numeric"
                  maxLength={1}
                  className="w-14 h-16 sm:w-16 sm:h-20 rounded-2xl bg-black/50 border-2 border-white/10 focus:border-eatup-yellow focus:bg-black/70 focus:ring-4 focus:ring-eatup-yellow/20 text-center text-white font-display font-black text-3xl sm:text-4xl tabular outline-none transition"
                />
              ))}
            </div>
            <button
              type="submit"
              disabled={busy || codeStr.length !== 4}
              data-testid="handoff-submit-btn"
              className="w-full sm:w-auto px-6 py-3 rounded-2xl bg-eatup-yellow text-eatup-charcoal font-display font-bold disabled:opacity-50 hover:brightness-110 active:scale-[0.98] transition flex items-center justify-center gap-2"
            >
              <Check className="w-4 h-4" />
              {busy ? "Verifying…" : "Verify & Release"}
            </button>
          </form>

          {readyForHandoff.length > 0 ? (
            <div className="mt-4 text-xs text-neutral-400">
              <span className="font-semibold text-white">{readyForHandoff.length}</span> driver
              {readyForHandoff.length !== 1 ? "s" : ""} waiting for handoff.
            </div>
          ) : (
            <div className="mt-3 text-xs text-neutral-500">
              No drivers waiting right now. This step unlocks the moment a driver accepts an order.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function Stat({ icon: Icon, label, value, onClick, testid }) {
  const clickable = !!onClick;
  const Wrapper = clickable ? "button" : "div";
  return (
    <Wrapper
      {...(clickable ? { onClick, "data-testid": testid, type: "button" } : { "data-testid": testid })}
      className={`text-left w-full bg-eatup-dark-surface border border-white/10 rounded-3xl p-5 transition ${
        clickable ? "hover:border-eatup-green/60 hover:bg-white/5 active:scale-[0.99] cursor-pointer" : ""
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-xl bg-eatup-green/20 text-eatup-green flex items-center justify-center">
          <Icon className="w-5 h-5" />
        </div>
        {clickable && <ChevronRight className="w-4 h-4 text-neutral-500" />}
      </div>
      <div className="mt-4 text-xs uppercase tracking-widest text-neutral-300 font-bold">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold tabular text-white">{value}</div>
      {clickable && (
        <div className="mt-2 text-[10px] uppercase tracking-widest text-eatup-green font-bold">View details →</div>
      )}
    </Wrapper>
  );
}

const PLATFORM_FEE_RATE = 0.15; // 15% platform commission on subtotal

function DrilldownView({ view, filtered, handedOffToday = [], onBack, onOpenOrder, onReportDelay }) {
  const today = new Date().toDateString();

  const todayOrders = useMemo(
    () => filtered
      .filter((o) => new Date(o.created_at).toDateString() === today)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [filtered, today],
  );
  const pendingOrders = useMemo(
    () => filtered
      .filter((o) => ["placed", "preparing", "ready"].includes(o.status) && !o.pickup_verified)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [filtered],
  );

  const completedTotal = handedOffToday.reduce((s, o) => s + (o.total || 0), 0);

  const title = view === "today_orders" ? "Today's orders"
    : view === "pending" ? "Pending orders"
    : view === "completed" ? "Completed today"
    : "Today's revenue breakdown";
  const subtitle = view === "today_orders" ? `${todayOrders.length} orders placed today`
    : view === "pending" ? `${pendingOrders.length} awaiting your kitchen`
    : view === "completed" ? `${handedOffToday.length} handed off · $${completedTotal.toFixed(2)} shipped out the door`
    : "Order-by-order earnings, platform fees, and net payout";

  return (
    <section className="mt-8" data-testid={`drilldown-${view}`}>
      <button
        onClick={onBack}
        data-testid="back-to-dashboard-btn"
        className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-200 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-4 py-2 transition"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Dashboard
      </button>

      <div className="mt-6 flex items-baseline justify-between gap-4">
        <div>
          <h2 className="font-display text-3xl font-bold text-white">{title}</h2>
          <div className="mt-1 text-sm text-neutral-400">{subtitle}</div>
        </div>
      </div>

      {view === "today_orders" && (
        <OrderList
          orders={todayOrders}
          emptyLabel="No orders placed today yet."
          onOpenOrder={onOpenOrder}
        />
      )}
      {view === "pending" && (
        <OrderList
          orders={pendingOrders}
          emptyLabel="You're all caught up — no pending orders."
          onOpenOrder={onOpenOrder}
          variant="pending"
          onReportDelay={onReportDelay}
        />
      )}
      {view === "revenue" && (
        <RevenueBreakdown orders={todayOrders} onOpenOrder={onOpenOrder} />
      )}
      {view === "completed" && (
        <CompletedTable orders={handedOffToday} onOpenOrder={onOpenOrder} totalValue={completedTotal} />
      )}
    </section>
  );
}

function CompletedTable({ orders, onOpenOrder, totalValue }) {
  if (!orders.length) {
    return (
      <div className="mt-6 bg-eatup-dark-surface border border-white/10 rounded-3xl p-10 text-center text-neutral-400" data-testid="completed-empty">
        No orders handed off yet today. Bring on the rush.
      </div>
    );
  }
  return (
    <div className="mt-6" data-testid="completed-table">
      {/* Summary strip */}
      <div className="mb-4 rounded-3xl bg-eatup-green/10 border-2 border-eatup-green/30 p-5 flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-black text-eatup-green">Handed off today</div>
          <div className="mt-1 font-display font-black text-3xl text-white tabular">{orders.length} <span className="text-lg text-neutral-400 font-semibold">orders</span></div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.3em] font-black text-eatup-green">Total value</div>
          <div className="mt-1 font-display font-black text-3xl text-white tabular">${totalValue.toFixed(2)}</div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-eatup-dark-surface border border-white/10 rounded-3xl overflow-hidden">
        <div className="grid grid-cols-[auto_1fr_auto_auto_auto] gap-4 items-center px-5 py-3 text-[10px] uppercase tracking-widest text-neutral-500 font-black border-b border-white/10 bg-white/5">
          <div>Order #</div>
          <div>Customer</div>
          <div>Handed off</div>
          <div>Status</div>
          <div className="text-right">Total</div>
        </div>
        {orders.map((o) => {
          const handedAt = o.pickup_verified_at ? new Date(o.pickup_verified_at) : null;
          return (
            <button
              key={o.order_id}
              onClick={() => onOpenOrder(o)}
              data-testid={`completed-row-${o.order_id}`}
              className="w-full grid grid-cols-[auto_1fr_auto_auto_auto] gap-4 items-center px-5 py-4 text-left border-b border-white/5 hover:bg-white/5 transition active:bg-white/10"
            >
              <div className="font-display font-black tabular text-eatup-yellow text-lg tracking-widest">
                #{o.order_id.slice(-6).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-white truncate">{o.customer_name}</div>
                <div className="text-xs text-neutral-500 truncate">
                  {o.items.length} item{o.items.length !== 1 ? "s" : ""} · Driver: {o.driver?.name || "assigned"}
                </div>
              </div>
              <div className="tabular text-sm text-neutral-300 font-semibold whitespace-nowrap">
                {handedAt
                  ? handedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                  : <span className="text-neutral-500">—</span>}
              </div>
              <DarkStatusBadge status={o.status} />
              <div className="text-right font-display font-bold text-white tabular whitespace-nowrap">
                ${o.total.toFixed(2)}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function OrderList({ orders, emptyLabel, onOpenOrder, variant, onReportDelay }) {
  if (!orders.length) {
    return (
      <div className="mt-6 bg-eatup-dark-surface border border-white/10 rounded-3xl p-10 text-center text-neutral-400" data-testid="drilldown-empty">
        {emptyLabel}
      </div>
    );
  }
  if (variant === "pending") {
    return <PendingOrderList orders={orders} onOpenOrder={onOpenOrder} onReportDelay={onReportDelay} />;
  }
  return (
    <div className="mt-6 grid gap-3">
      {orders.map((o) => (
        <button
          key={o.order_id}
          onClick={() => onOpenOrder(o)}
          data-testid={`drilldown-order-${o.order_id}`}
          className="w-full text-left bg-eatup-dark-surface border border-white/10 hover:border-eatup-green/60 rounded-3xl p-5 transition flex items-center gap-4 active:scale-[0.995]"
        >
          <div className="w-12 h-12 rounded-2xl bg-eatup-charcoal text-eatup-yellow flex items-center justify-center shrink-0">
            <Receipt className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-widest text-eatup-green font-bold">
                #{o.order_id.slice(-6).toUpperCase()}
              </span>
              <DarkStatusBadge status={o.status} />
              <span className="text-[10px] text-neutral-500">
                {new Date(o.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
            <div className="mt-1 font-semibold text-white truncate">{o.customer_name}</div>
            <div className="text-xs text-neutral-400 truncate">
              {o.items.length} item{o.items.length !== 1 ? "s" : ""} · {o.address}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-display text-lg font-bold text-white tabular">${o.total.toFixed(2)}</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold capitalize">
              {o.payment_status}
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-neutral-500 shrink-0" />
        </button>
      ))}
    </div>
  );
}

function PendingOrderList({ orders, onOpenOrder, onReportDelay }) {
  // Kitchen-optimised card: massive Order # on the left so staff can call it out
  // from a few feet away. Pickup PIN badge is shown right next to it once a
  // driver has been assigned, so the runner can verify at a glance.
  return (
    <div className="mt-6 grid gap-4">
      {orders.map((o) => {
        const orderNo = o.order_id.slice(-6).toUpperCase();
        const timeLabel = new Date(o.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        const pickupPin = o.pickup_code ? String(o.pickup_code).padStart(4, "0") : null;
        const delayedBy = Number(o.delayed_by_minutes || 0);
        return (
          <div
            key={o.order_id}
            data-testid={`pending-card-${o.order_id}`}
            className="group w-full text-left bg-eatup-dark-surface border-2 border-white/10 hover:border-eatup-yellow/70 rounded-3xl overflow-hidden transition"
          >
            <button
              onClick={() => onOpenOrder(o)}
              data-testid={`drilldown-order-${o.order_id}`}
              className="w-full text-left active:scale-[0.997]"
            >
              <div className="grid grid-cols-[auto_1fr_auto] items-stretch">
                {/* LEFT: giant order number — the visual anchor, readable from feet away */}
                <div className="relative bg-eatup-charcoal border-r border-white/10 px-6 py-5 flex flex-col items-center justify-center shrink-0 min-w-[180px]">
                  <div className="text-[9px] uppercase tracking-[0.35em] text-eatup-yellow/80 font-black">Order #</div>
                  <div
                    data-testid={`pending-order-number-${o.order_id}`}
                    className="mt-1 font-display font-black tabular text-eatup-yellow text-4xl sm:text-5xl leading-none drop-shadow-[0_2px_8px_rgba(240,196,25,0.35)] select-all"
                  >
                    {orderNo}
                  </div>
                  <div className="mt-2 flex items-center gap-1 text-[10px] uppercase tracking-widest text-neutral-400 font-bold">
                    <Clock className="w-3 h-3" /> {timeLabel}
                  </div>
                </div>

                {/* MIDDLE: customer + items */}
                <div className="min-w-0 px-5 py-5 flex flex-col justify-center gap-1.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <DarkStatusBadge status={o.status} />
                    {pickupPin && (
                      <span
                        data-testid={`pending-pickup-pin-${o.order_id}`}
                        className="inline-flex items-center gap-1.5 bg-eatup-yellow/15 border border-eatup-yellow/40 text-eatup-yellow font-black tabular text-sm sm:text-base px-3 py-1 rounded-full tracking-[0.3em]"
                        title="Driver pickup PIN — verify with the runner"
                      >
                        <Shield className="w-3.5 h-3.5" /> PIN {pickupPin}
                      </span>
                    )}
                    {delayedBy > 0 && (
                      <span
                        data-testid={`pending-delay-badge-${o.order_id}`}
                        className="inline-flex items-center gap-1 bg-red-500/15 border border-red-500/40 text-red-300 font-bold text-[11px] px-2.5 py-0.5 rounded-full uppercase tracking-widest"
                        title="Delay reported to driver & customer"
                      >
                        <Clock className="w-3 h-3" /> +{delayedBy} min delay
                      </span>
                    )}
                  </div>
                  <div className="font-display font-bold text-lg sm:text-xl text-white truncate leading-tight">
                    {o.customer_name}
                  </div>
                  <div className="text-xs sm:text-sm text-neutral-400 truncate">
                    {o.items.length} item{o.items.length !== 1 ? "s" : ""} · {o.address}
                  </div>
                </div>

                {/* RIGHT: total + chevron */}
                <div className="px-5 py-5 flex flex-col items-end justify-center gap-1 shrink-0 border-l border-white/5">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Total</div>
                  <div className="font-display font-black text-2xl sm:text-3xl text-white tabular leading-none">
                    ${o.total.toFixed(2)}
                  </div>
                  <ChevronRight className="mt-2 w-5 h-5 text-neutral-500 group-hover:text-eatup-yellow transition" />
                </div>
              </div>
            </button>

            {/* Report Delay strip — visible only while the food is still in the kitchen */}
            {["placed", "preparing", "ready"].includes(o.status) && onReportDelay && (
              <div className="border-t border-white/10 bg-black/20 px-5 py-3 flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-black text-neutral-300">
                  <Clock className="w-3.5 h-3.5 text-red-300" /> Kitchen delay?
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <button
                    data-testid={`pending-report-delay-10-${o.order_id}`}
                    onClick={(e) => { e.stopPropagation(); onReportDelay(o.order_id, 10); }}
                    className="text-[11px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full bg-red-500/15 border border-red-500/40 text-red-200 hover:bg-red-500/25 transition"
                  >
                    + 10 min
                  </button>
                  <button
                    data-testid={`pending-report-delay-20-${o.order_id}`}
                    onClick={(e) => { e.stopPropagation(); onReportDelay(o.order_id, 20); }}
                    className="text-[11px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full bg-red-500/20 border border-red-500/50 text-red-100 hover:bg-red-500/30 transition"
                  >
                    + 20 min
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function RevenueBreakdown({ orders, onOpenOrder }) {
  const rows = useMemo(() => orders.map((o) => {
    const subtotal = o.subtotal || 0;
    const platformFee = Math.round(subtotal * PLATFORM_FEE_RATE * 100) / 100;
    const net = Math.round((subtotal - platformFee) * 100) / 100;
    return { order: o, subtotal, platformFee, net };
  }), [orders]);

  const totals = useMemo(() => rows.reduce((acc, r) => ({
    gross: acc.gross + (r.order.total || 0),
    subtotal: acc.subtotal + r.subtotal,
    fee: acc.fee + r.platformFee,
    net: acc.net + r.net,
  }), { gross: 0, subtotal: 0, fee: 0, net: 0 }), [rows]);

  return (
    <div className="mt-6 space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryTile testid="rev-total-gross" label="Customer gross" value={`$${totals.gross.toFixed(2)}`} hint="Amount paid by customers" tone="text-white" />
        <SummaryTile testid="rev-total-subtotal" label="Food subtotal" value={`$${totals.subtotal.toFixed(2)}`} hint="Menu-item revenue only" tone="text-white" />
        <SummaryTile testid="rev-total-fee" label={`Platform fee (${Math.round(PLATFORM_FEE_RATE * 100)}%)`} value={`- $${totals.fee.toFixed(2)}`} hint="Deducted by Eats GND" tone="text-red-400" icon={Percent} />
        <SummaryTile testid="rev-total-net" label="Net earnings" value={`$${totals.net.toFixed(2)}`} hint="Payout to your restaurant" tone="text-eatup-green" highlight />
      </div>

      {rows.length === 0 ? (
        <div className="bg-eatup-dark-surface border border-white/10 rounded-3xl p-10 text-center text-neutral-400" data-testid="drilldown-empty">
          No paid orders today yet — your kitchen is waiting.
        </div>
      ) : (
        <div className="bg-eatup-dark-surface border border-white/10 rounded-3xl overflow-hidden">
          <div className="hidden md:grid grid-cols-[1.2fr_1fr_1fr_1fr_1fr_auto] gap-4 px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-widest text-neutral-400 font-bold">
            <div>Order</div>
            <div className="text-right">Subtotal</div>
            <div className="text-right">Customer total</div>
            <div className="text-right">Platform fee</div>
            <div className="text-right">Net earnings</div>
            <div className="w-4" />
          </div>
          {rows.map(({ order, subtotal, platformFee, net }) => (
            <button
              key={order.order_id}
              data-testid={`rev-row-${order.order_id}`}
              onClick={() => onOpenOrder(order)}
              className="w-full text-left grid grid-cols-1 md:grid-cols-[1.2fr_1fr_1fr_1fr_1fr_auto] gap-4 items-center px-5 py-4 border-b border-white/5 last:border-b-0 hover:bg-white/5 transition"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest text-eatup-green font-bold">
                    #{order.order_id.slice(-6).toUpperCase()}
                  </span>
                  <DarkStatusBadge status={order.status} />
                </div>
                <div className="mt-0.5 font-semibold text-white truncate">{order.customer_name}</div>
                <div className="text-[11px] text-neutral-500">
                  {new Date(order.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  {" · "}{order.items.length} item{order.items.length !== 1 ? "s" : ""}
                </div>
              </div>
              <div className="md:text-right text-sm text-neutral-200 tabular">
                <span className="md:hidden text-[10px] uppercase tracking-widest text-neutral-500 font-bold mr-2">Subtotal</span>
                ${subtotal.toFixed(2)}
              </div>
              <div className="md:text-right text-sm text-neutral-200 tabular">
                <span className="md:hidden text-[10px] uppercase tracking-widest text-neutral-500 font-bold mr-2">Customer</span>
                ${order.total.toFixed(2)}
              </div>
              <div className="md:text-right text-sm text-red-400 tabular">
                <span className="md:hidden text-[10px] uppercase tracking-widest text-neutral-500 font-bold mr-2">Fee</span>
                - ${platformFee.toFixed(2)}
              </div>
              <div className="md:text-right font-display font-bold text-eatup-green tabular">
                <span className="md:hidden text-[10px] uppercase tracking-widest text-neutral-500 font-bold mr-2">Net</span>
                ${net.toFixed(2)}
              </div>
              <ChevronRight className="hidden md:block w-4 h-4 text-neutral-500 justify-self-end" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryTile({ label, value, hint, tone = "text-white", icon: Icon, highlight, testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-3xl p-5 border ${highlight ? "bg-eatup-green/10 border-eatup-green/40" : "bg-eatup-dark-surface border-white/10"}`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-300 font-bold">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`mt-2 font-display text-2xl font-bold tabular ${tone}`}>{value}</div>
      <div className="mt-1 text-[11px] text-neutral-500">{hint}</div>
    </div>
  );
}

function DarkStatusBadge({ status }) {
  const map = {
    placed: "bg-white/10 text-neutral-200",
    preparing: "bg-amber-500/20 text-amber-300",
    ready: "bg-blue-500/20 text-blue-300",
    picked_up: "bg-purple-500/20 text-purple-300",
    on_the_way: "bg-purple-500/20 text-purple-300",
    delivered: "bg-eatup-green/20 text-eatup-green",
    cancelled: "bg-red-500/20 text-red-300",
  };
  return (
    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${map[status] || "bg-white/10 text-neutral-200"}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function AutoDelayCheckModal({ order, minutesLate, addMinutes, onYes, onNo }) {
  // Called after AUTO_DELAY_THRESHOLD_MIN — asks the manager if the order is
  // actually running late. Confirming triggers the same +N min delay flow that
  // the manual "Report Delay" buttons use, but flagged auto_triggered:true so
  // the admin dashboard can chart auto-check confirmations separately.
  const [busy, setBusy] = useState(false);
  const orderNo = order.order_id.slice(-6).toUpperCase();
  const handleYes = async () => {
    if (busy) return;
    setBusy(true);
    try { await onYes(); } finally { setBusy(false); }
  };
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="auto-delay-check-modal"
    >
      <div className="bg-white text-eatup-charcoal w-full max-w-md rounded-3xl shadow-2xl overflow-hidden fade-up">
        <div className="bg-gradient-to-br from-red-500 to-red-600 text-white px-6 py-6">
          <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mb-3">
            <Clock className="w-7 h-7 text-white" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-black text-white/80">
            Automated delay check
          </div>
          <h2 className="mt-1 font-display font-black text-2xl leading-tight">
            Is order #{orderNo} running late?
          </h2>
          <div className="mt-2 text-sm text-white/90">
            It's been in the kitchen for <span className="font-black tabular">{minutesLate} min</span> and still hasn't hit <span className="font-black">Ready</span>.
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="rounded-2xl bg-neutral-100 p-4 flex items-center gap-3">
            <img src={order.restaurant_image} alt="" className="w-12 h-12 rounded-xl object-cover" />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Customer</div>
              <div className="font-semibold truncate">{order.customer_name}</div>
              <div className="text-xs text-neutral-500 truncate">
                {order.items.length} item{order.items.length !== 1 ? "s" : ""} · ${order.total.toFixed(2)}
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-red-50 border border-red-200 p-3 text-xs text-red-700">
            Clicking <span className="font-black">Yes, running late</span> automatically bumps the
            customer's ETA by <span className="font-black">+{addMinutes} min</span> and notifies the driver.
            The incident is logged for the admin dashboard.
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={onNo}
              disabled={busy}
              data-testid="auto-delay-no-btn"
              className="py-3 rounded-full bg-neutral-100 hover:bg-neutral-200 font-semibold text-sm transition disabled:opacity-60"
            >
              No, still on time
            </button>
            <button
              onClick={handleYes}
              disabled={busy}
              data-testid="auto-delay-yes-btn"
              className="py-3 rounded-full bg-red-600 hover:bg-red-700 text-white font-black text-sm transition disabled:opacity-60 flex items-center justify-center gap-2"
            >
              <Clock className="w-4 h-4" />
              {busy ? "Reporting…" : `Yes · +${addMinutes} min`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function MenuPreview({ restaurantId }) {
  const [menu, setMenu] = useState([]);
  useEffect(() => {
    api.get(`/restaurants/${restaurantId}`).then((r) => setMenu(r.data.menu || [])).catch(() => {});
  }, [restaurantId]);
  return (
    <section className="mt-10">
      <h2 className="font-display text-2xl font-bold text-white">Menu preview</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {menu.slice(0, 6).map((m) => (
          <div key={m.item_id} className="bg-eatup-dark-surface border border-white/10 rounded-2xl p-3 flex gap-3 items-center">
            <img src={m.image_url} alt="" className="w-14 h-14 rounded-xl object-cover" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate text-white">{m.name}</div>
              <div className="text-xs text-neutral-400">${m.price.toFixed(2)} · {m.category}</div>
            </div>
            <div className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full ${m.available ? "bg-eatup-green/20 text-eatup-green" : "bg-red-500/20 text-red-400"}`}>
              {m.available ? "Live" : "Off"}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Menu Manager — full CRUD for menu items scoped to a single restaurant.
// -----------------------------------------------------------------------------
function MenuManager({ restaurantId, restaurantName, onBack }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | item object
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [query, setQuery] = useState("");

  const load = async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const r = await api.get(`/restaurant/menu?restaurant_id=${restaurantId}`);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [restaurantId]);

  const categories = useMemo(() => {
    const s = new Set(items.map((i) => i.category).filter(Boolean));
    return ["All", ...Array.from(s).sort()];
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (categoryFilter !== "All" && it.category !== categoryFilter) return false;
      if (query && !`${it.name} ${it.description}`.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
  }, [items, categoryFilter, query]);

  const toggleAvailability = async (it) => {
    try {
      await api.patch(`/restaurant/menu/${it.item_id}/availability`, { available: !it.available });
      setItems((prev) => prev.map((x) => x.item_id === it.item_id ? { ...x, available: !x.available } : x));
      toast.success(!it.available ? "Item is now live" : "Item paused");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteItem = async (it) => {
    if (!window.confirm(`Delete "${it.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/restaurant/menu/${it.item_id}`);
      setItems((prev) => prev.filter((x) => x.item_id !== it.item_id));
      toast.success("Menu item deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!restaurantId) {
    return (
      <div className="mt-4 p-8 rounded-3xl border border-white/10 bg-eatup-dark-surface text-center text-neutral-300">
        Select a restaurant above to manage its menu.
      </div>
    );
  }

  return (
    <section data-testid="menu-manager" className="mt-2">
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onBack}
          data-testid="menu-back-btn"
          className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition"
          aria-label="Back to dashboard"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-widest text-eatup-green">Menu Manager</div>
          <h2 className="font-display text-3xl font-bold text-white truncate">{restaurantName}</h2>
        </div>
        <button
          data-testid="menu-add-btn"
          onClick={() => setEditing("new")}
          className="px-4 py-2.5 rounded-full bg-eatup-green text-black font-bold text-sm hover:brightness-110 transition inline-flex items-center gap-1.5"
        >
          <Utensils className="w-4 h-4" /> Add item
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            data-testid="menu-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search menu items…"
            className="w-full pl-10 pr-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-eatup-green"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`menu-cat-chip-${c}`}
              onClick={() => setCategoryFilter(c)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest transition ${
                categoryFilter === c ? "bg-eatup-green text-black" : "bg-white/5 text-neutral-300 hover:bg-white/10"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[0,1,2,3,4,5].map((i) => (
            <div key={i} className="h-32 rounded-2xl bg-white/5 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-12 rounded-3xl border border-dashed border-white/10 bg-eatup-dark-surface text-center">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-eatup-green/15 flex items-center justify-center mb-3">
            <Utensils className="w-6 h-6 text-eatup-green" />
          </div>
          <div className="font-semibold text-white text-lg">No menu items yet</div>
          <div className="text-sm text-neutral-400 mt-1">Add your first dish so customers can order it.</div>
          <button
            data-testid="menu-empty-add-btn"
            onClick={() => setEditing("new")}
            className="mt-5 px-5 py-2.5 rounded-full bg-eatup-green text-black font-bold text-sm hover:brightness-110 transition"
          >
            Add first item
          </button>
        </div>
      ) : (
        <div data-testid="menu-items-grid" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((it) => (
            <MenuItemCard
              key={it.item_id}
              item={it}
              onEdit={() => setEditing(it)}
              onDelete={() => deleteItem(it)}
              onToggle={() => toggleAvailability(it)}
            />
          ))}
        </div>
      )}

      {editing && (
        <MenuItemFormModal
          restaurantId={restaurantId}
          existingItem={editing === "new" ? null : editing}
          categories={categories.filter((c) => c !== "All")}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </section>
  );
}

function MenuItemCard({ item, onEdit, onDelete, onToggle }) {
  return (
    <div
      data-testid={`menu-item-card-${item.item_id}`}
      className="bg-eatup-dark-surface border border-white/10 rounded-2xl overflow-hidden group hover:border-eatup-green/40 transition"
    >
      <div className="relative h-32 bg-white/5">
        {item.image_url ? (
          <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-500">
            <Utensils className="w-8 h-8" />
          </div>
        )}
        <button
          data-testid={`menu-toggle-${item.item_id}`}
          onClick={onToggle}
          className={`absolute top-2 right-2 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${
            item.available ? "bg-eatup-green text-black" : "bg-red-500/90 text-white"
          }`}
        >
          {item.available ? "Live" : "Off"}
        </button>
      </div>
      <div className="p-3">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-white truncate">{item.name}</div>
            <div className="text-[11px] text-neutral-400 uppercase tracking-widest">{item.category}</div>
          </div>
          <div className="font-display text-lg font-bold text-eatup-green tabular-nums">EC${Number(item.price || 0).toFixed(2)}</div>
        </div>
        {item.description && (
          <div className="text-xs text-neutral-400 mt-1.5 line-clamp-2">{item.description}</div>
        )}
        <div className="mt-3 flex gap-2">
          <button
            data-testid={`menu-edit-${item.item_id}`}
            onClick={onEdit}
            className="flex-1 py-2 rounded-full bg-white/5 hover:bg-white/10 text-white text-xs font-bold uppercase tracking-widest transition"
          >
            Edit
          </button>
          <button
            data-testid={`menu-delete-${item.item_id}`}
            onClick={onDelete}
            className="px-3 py-2 rounded-full bg-red-500/10 hover:bg-red-500/25 text-red-400 text-xs font-bold uppercase tracking-widest transition"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function MenuItemFormModal({ restaurantId, existingItem, categories, onClose, onSaved }) {
  const isEdit = !!existingItem;
  const [name, setName] = useState(existingItem?.name || "");
  const [description, setDescription] = useState(existingItem?.description || "");
  const [price, setPrice] = useState(existingItem?.price ?? "");
  const [category, setCategory] = useState(existingItem?.category || (categories[0] || "Main"));
  const [imageUrl, setImageUrl] = useState(existingItem?.image_url || "");
  const [available, setAvailable] = useState(existingItem?.available ?? true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Photo too large (max 8 MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/restaurant/menu/upload-photo?restaurant_id=${restaurantId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = r.data.photo_url;
      // Absolute URL so it works on the customer app too.
      setImageUrl(url.startsWith("http") ? url : `${backendUrl}${url}`);
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    const priceNum = Number(price);
    if (Number.isNaN(priceNum) || priceNum < 0) { toast.error("Price must be a positive number"); return; }
    if (!category.trim()) { toast.error("Category is required"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/restaurant/menu/${existingItem.item_id}`, {
          name: name.trim(),
          description: description.trim(),
          price: priceNum,
          category: category.trim(),
          image_url: imageUrl,
          available,
        });
        toast.success("Menu item updated");
      } else {
        await api.post("/restaurant/menu", {
          restaurant_id: restaurantId,
          name: name.trim(),
          description: description.trim(),
          price: priceNum,
          category: category.trim(),
          image_url: imageUrl,
          available,
        });
        toast.success("Menu item added");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="menu-form-modal"
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-lg bg-eatup-dark-surface border border-white/10 rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
          <div className="flex-1">
            <div className="text-[10px] font-bold uppercase tracking-widest text-eatup-green">{isEdit ? "Edit item" : "New item"}</div>
            <div className="font-display text-xl font-bold text-white">{isEdit ? name || "Edit menu item" : "Add menu item"}</div>
          </div>
          <button data-testid="menu-form-close-btn" onClick={onClose} className="p-2 rounded-full hover:bg-white/10">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Photo upload */}
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-neutral-400">Photo</label>
            <div className="mt-2 flex items-center gap-3">
              <div className="w-24 h-24 rounded-2xl overflow-hidden bg-white/5 border border-white/10 flex-shrink-0 relative">
                {imageUrl ? (
                  <img src={imageUrl} alt="" className="w-full h-full object-cover" data-testid="menu-form-image-preview" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-neutral-500">
                    <Utensils className="w-6 h-6" />
                  </div>
                )}
                {uploading && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <Loader2 className="w-5 h-5 animate-spin text-eatup-green" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                  onChange={(e) => uploadPhoto(e.target.files?.[0])}
                  className="hidden"
                  data-testid="menu-form-file-input"
                />
                <button
                  data-testid="menu-form-upload-btn"
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full px-3.5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-bold uppercase tracking-widest transition disabled:opacity-50"
                >
                  {uploading ? "Uploading…" : imageUrl ? "Replace photo" : "Upload photo"}
                </button>
                <div className="text-[11px] text-neutral-500 mt-1.5">JPG · PNG · WebP · max 8 MB</div>
              </div>
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-neutral-400">Name</label>
            <input
              data-testid="menu-form-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              placeholder="Jerk Chicken"
              className="mt-1.5 w-full px-4 py-2.5 rounded-2xl bg-white/5 border border-white/10 text-white outline-none focus:border-eatup-green"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-neutral-400">Description</label>
            <textarea
              data-testid="menu-form-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Slow-marinated in scotch bonnet & pimento…"
              className="mt-1.5 w-full px-4 py-2.5 rounded-2xl bg-white/5 border border-white/10 text-white outline-none focus:border-eatup-green resize-none"
            />
          </div>

          {/* Price + Category */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-neutral-400">Price (EC$)</label>
              <input
                data-testid="menu-form-price"
                type="number"
                step="0.01"
                min="0"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="15.00"
                className="mt-1.5 w-full px-4 py-2.5 rounded-2xl bg-white/5 border border-white/10 text-white outline-none focus:border-eatup-green tabular-nums"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-neutral-400">Category</label>
              <input
                data-testid="menu-form-category"
                list="menu-category-suggestions"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="Main"
                className="mt-1.5 w-full px-4 py-2.5 rounded-2xl bg-white/5 border border-white/10 text-white outline-none focus:border-eatup-green"
              />
              <datalist id="menu-category-suggestions">
                {categories.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
          </div>

          {/* Availability toggle */}
          <button
            type="button"
            data-testid="menu-form-available-toggle"
            onClick={() => setAvailable((v) => !v)}
            className={`w-full px-4 py-3 rounded-2xl border transition text-left flex items-center justify-between ${
              available
                ? "bg-eatup-green/10 border-eatup-green/40 text-white"
                : "bg-red-500/5 border-red-500/30 text-white"
            }`}
          >
            <div>
              <div className="text-sm font-semibold">{available ? "Available for ordering" : "Hidden from customers"}</div>
              <div className="text-[11px] text-neutral-400 mt-0.5">Tap to toggle live status</div>
            </div>
            <div className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${available ? "bg-eatup-green text-black" : "bg-red-500 text-white"}`}>
              {available ? "Live" : "Off"}
            </div>
          </button>
        </div>

        <div className="p-5 border-t border-white/10 flex gap-3">
          <button
            data-testid="menu-form-cancel-btn"
            onClick={onClose}
            className="flex-1 py-3 rounded-full bg-white/5 hover:bg-white/10 text-white text-sm font-bold uppercase tracking-widest transition"
          >
            Cancel
          </button>
          <button
            data-testid="menu-form-save-btn"
            onClick={save}
            disabled={saving || uploading}
            className="flex-1 py-3 rounded-full bg-eatup-green text-black text-sm font-bold uppercase tracking-widest hover:brightness-110 transition disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? "Save changes" : "Add item"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BatchDelayModal({ orders, onAcknowledgeAll, onDismiss }) {
  // Batch acknowledge — one delay choice applied to every currently overdue
  // order. Restaurants pick a preset (+10 / +15 / +20 min), tap "Acknowledge
  // all", and every affected customer & driver is notified in the same beat.
  const [choice, setChoice] = useState(15);
  const [busy, setBusy] = useState(false);

  const handleAck = async () => {
    if (busy) return;
    setBusy(true);
    try { await onAcknowledgeAll(choice); } finally { setBusy(false); }
  };

  const oldestMinutes = orders.reduce((max, o) => {
    const m = Math.floor((Date.now() - new Date(o.created_at).getTime()) / 60000);
    return Math.max(max, m);
  }, 0);

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="batch-delay-modal"
    >
      <div className="bg-white text-eatup-charcoal w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden fade-up flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="bg-gradient-to-br from-red-500 to-red-600 text-white px-6 py-6 shrink-0">
          <div className="flex items-start gap-3">
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
              <Clock className="w-7 h-7 text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] font-black text-white/80">
                Automated delay check · Batch
              </div>
              <h2 className="mt-1 font-display font-black text-2xl leading-tight">
                {orders.length} orders are running late
              </h2>
              <div className="mt-2 text-sm text-white/90">
                Oldest has been in the kitchen for <span className="font-black tabular">{oldestMinutes} min</span>. Pick a delay and acknowledge them all at once.
              </div>
            </div>
          </div>
        </div>

        {/* Order list */}
        <div className="flex-1 overflow-y-auto px-6 py-4 border-b border-neutral-100">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-black mb-2">
            Affected orders
          </div>
          <div className="space-y-2">
            {orders.map((o) => {
              const orderNo = o.order_id.slice(-6).toUpperCase();
              const ageMins = Math.floor((Date.now() - new Date(o.created_at).getTime()) / 60000);
              return (
                <div
                  key={o.order_id}
                  data-testid={`batch-delay-row-${o.order_id}`}
                  className="flex items-center gap-3 rounded-2xl bg-neutral-100 px-3 py-2.5"
                >
                  <div className="w-10 h-10 rounded-xl bg-eatup-charcoal text-white flex items-center justify-center font-bold shrink-0">
                    {(o.customer_name || "?")[0].toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate flex items-center gap-2">
                      <span className="text-eatup-yellow font-black tabular text-sm tracking-widest shrink-0">#{orderNo}</span>
                      <span className="truncate">{o.customer_name}</span>
                    </div>
                    <div className="text-xs text-neutral-500 truncate">
                      {o.items.length} item{o.items.length !== 1 ? "s" : ""} · in kitchen {ageMins} min
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Was</div>
                    <div className="text-sm font-bold tabular">{o.eta_minutes || 0}m</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Delay picker */}
        <div className="px-6 py-4 shrink-0 space-y-3">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-black">
            Apply the same delay to all
          </div>
          <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Delay minutes">
            {[10, 15, 20].map((m) => {
              const active = choice === m;
              return (
                <button
                  key={m}
                  role="radio"
                  aria-checked={active}
                  data-testid={`batch-delay-choice-${m}`}
                  onClick={() => setChoice(m)}
                  className={`rounded-2xl py-3 font-display font-black text-lg transition ${
                    active
                      ? "bg-red-600 text-white shadow-lg"
                      : "bg-neutral-100 hover:bg-neutral-200 text-eatup-charcoal"
                  }`}
                >
                  + {m} min
                </button>
              );
            })}
          </div>

          <div className="text-[11px] text-neutral-500">
            Every affected customer and driver will be notified instantly. Each order gets a
            <span className="font-black text-red-600"> +{choice} min</span> ETA bump.
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-100 flex items-center gap-2 shrink-0">
          <button
            data-testid="batch-delay-dismiss-btn"
            onClick={onDismiss}
            disabled={busy}
            className="px-4 py-3 rounded-full text-sm font-bold text-neutral-600 hover:bg-neutral-100 transition disabled:opacity-60"
          >
            Not now
          </button>
          <button
            data-testid="batch-delay-acknowledge-btn"
            onClick={handleAck}
            disabled={busy}
            className="ml-auto flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-red-600 hover:bg-red-700 text-white font-black text-sm uppercase tracking-widest transition disabled:opacity-60"
          >
            <Clock className="w-4 h-4" />
            {busy ? "Notifying…" : `Acknowledge all · +${choice} min`}
          </button>
        </div>
      </div>
    </div>
  );
}


function IncomingOrderModal({ order, onAccept, onReject, onDismiss }) {
  const ORDER_SECONDS = 120;
  const [countdown, setCountdown] = useState(ORDER_SECONDS);
  const [busy, setBusy] = useState(null); // null | "accept" | "reject" — disables both buttons while an API call is in flight
  const alertRef = useRef(null);

  const stopAlert = () => {
    if (alertRef.current) {
      try { alertRef.current.stop(); } catch { /* noop */ }
      alertRef.current = null;
    }
  };

  useEffect(() => {
    // Start the looping Caribbean-vibe alert (30% → 100% over 30s).
    alertRef.current = startCaribbeanAlert();
    return () => stopAlert();
  }, []);

  useEffect(() => {
    if (busy) return; // freeze the timer while a network call is running
    if (countdown <= 0) { stopAlert(); onDismiss(); return; }
    const t = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, onDismiss, busy]);

  // Guarded action runner — swallows double-clicks and lets the parent's
  // async handler complete (or throw) before we re-enable the buttons.
  const run = (kind, fn) => async () => {
    if (busy) return;
    setBusy(kind);
    stopAlert();
    try {
      await fn();
      // Parent handles unmount by clearing the modal state on success.
    } catch {
      // Re-enable the buttons so the user can retry.
      setBusy(null);
    }
  };

  const mins = Math.floor(countdown / 60);
  const secs = countdown % 60;
  const timeLabel = mins > 0 ? `${mins}:${String(secs).padStart(2, "0")}` : `${secs}s`;
  const urgent = countdown <= 20;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-6 fade-up"
      data-testid="incoming-order-modal"
      role="dialog"
      aria-modal="true"
    >
      {/* Blurred, darkened backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-2xl backdrop-saturate-150" />

      {/* Floating glass card */}
      <div
        className={`relative w-full max-w-md sm:max-w-lg rounded-[32px] border-2 pulse-sunset-border p-6 sm:p-8 shadow-[0_40px_120px_rgba(0,0,0,0.6)] ring-1 ring-white/10 flex flex-col max-h-[92vh] overflow-hidden ${
          urgent ? "border-red-400/70" : "border-eatup-yellow/60"
        }`}
        style={{
          background:
            "radial-gradient(120% 120% at 0% 0%, rgba(242,201,76,0.20) 0%, rgba(24,180,91,0.10) 45%, rgba(20,20,22,0.85) 100%)",
          backdropFilter: "blur(40px) saturate(180%)",
          WebkitBackdropFilter: "blur(40px) saturate(180%)",
        }}
      >
        {/* Header pulse indicator */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-eatup-yellow opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-eatup-yellow" />
            </span>
            <span className="text-[10px] uppercase tracking-[0.4em] font-black text-eatup-yellow">Ka-ching · new order</span>
          </div>
          <div className={`flex items-center gap-1.5 text-xs font-bold tabular ${urgent ? "text-red-400 animate-pulse" : "text-white"}`}>
            <Clock className="w-3.5 h-3.5" /> {timeLabel}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto mt-5 flex flex-col items-center">
          <div className="relative w-36 h-36 sm:w-40 sm:h-40 rounded-full pulse-sunset">
            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
              <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="6" />
              <circle cx="50" cy="50" r="46" fill="none"
                stroke={urgent ? "#EF4444" : "#F2C94C"} strokeWidth="6" strokeLinecap="round"
                strokeDasharray={289}
                strokeDashoffset={289 * (1 - countdown / ORDER_SECONDS)}
                className="transition-all duration-1000 linear"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <Bell className="w-8 h-8 text-eatup-yellow animate-pulse drop-shadow-[0_0_12px_rgba(242,201,76,0.7)]" />
            </div>
          </div>

          <h2 className="mt-5 font-display text-2xl sm:text-3xl font-black text-white text-center drop-shadow-md">
            New order — {order.restaurant_name}
          </h2>
          <div className="mt-1 text-sm text-neutral-300">from {order.customer_name}</div>

          <div className="mt-5 w-full rounded-3xl bg-black/40 border border-white/15 p-4 sm:p-5 text-left space-y-3 backdrop-blur-md">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-300 font-bold">Total</div>
                <div className="font-display font-black text-2xl tabular text-white">${order.total.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-300 font-bold">Items</div>
                <div className="font-display font-black text-2xl tabular text-white">{order.items.length}</div>
              </div>
            </div>
            <div className="border-t border-white/10 pt-3 space-y-1.5 text-sm">
              {order.items.slice(0, 5).map((it) => (
                <div key={it.item_id} className="flex items-center justify-between">
                  <span className="text-white truncate mr-2">{it.quantity}× {it.name}</span>
                  <span className="text-neutral-300 tabular shrink-0">${(it.price * it.quantity).toFixed(2)}</span>
                </div>
              ))}
              {order.items.length > 5 && <div className="text-xs text-neutral-400">+{order.items.length - 5} more items</div>}
            </div>
            <div className="border-t border-white/10 pt-3 flex items-start gap-2 text-xs">
              <MapPin className="w-3.5 h-3.5 text-eatup-green shrink-0 mt-0.5" />
              <span className="text-neutral-200">{order.address}</span>
            </div>
            {order.instructions && (
              <div className="rounded-xl bg-eatup-yellow/15 border border-eatup-yellow/30 p-2 text-xs">
                <span className="font-bold text-eatup-yellow">Note:</span> <span className="text-white">{order.instructions}</span>
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 space-y-2.5 shrink-0">
          <button
            onClick={run("accept", onAccept)}
            disabled={busy !== null}
            data-testid="incoming-accept-btn"
            className="w-full bg-eatup-green hover:bg-eatup-green-dark disabled:opacity-70 disabled:cursor-not-allowed rounded-full py-4 font-display font-black text-lg text-white transition shadow-[0_10px_30px_rgba(24,180,91,0.4)] active:scale-[0.98] flex items-center justify-center gap-2"
          >
            {busy === "accept" ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" /> Accepting…
              </>
            ) : (
              <>
                <Check className="w-5 h-5" /> Accept &amp; start preparing
              </>
            )}
          </button>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => { if (!busy) { stopAlert(); onDismiss(); } }}
              disabled={busy !== null}
              data-testid="incoming-dismiss-btn"
              className="py-3 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-70 disabled:cursor-not-allowed border border-white/10 text-white font-semibold text-sm transition"
            >
              Snooze
            </button>
            <button
              onClick={run("reject", onReject)}
              disabled={busy !== null}
              data-testid="incoming-reject-btn"
              className="py-3 rounded-full text-red-300 hover:bg-red-500/10 disabled:opacity-70 disabled:cursor-not-allowed font-semibold text-sm transition border border-red-500/30"
            >
              {busy === "reject" ? "Declining…" : "Decline"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RestaurantOrderDetails({ order, onClose, onSetStatus, onVerifyPickup, onReportDelay }) {
  const [copied, setCopied] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [openContact, setOpenContact] = useState(null); // "customer" | "driver" | null
  const timeline = order.timeline || [];

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(order.address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const printTicket = () => {
    window.print();
    toast.success("Kitchen ticket sent to printer");
  };

  const nextStatus = NEXT_STATUS[order.status];
  const canCancel = !["delivered", "cancelled", "picked_up"].includes(order.status);
  const canReportDelay = ["placed", "preparing", "ready"].includes(order.status);
  const totalDelay = Number(order.delayed_by_minutes || 0);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="rest-order-details-sheet"
    >
      <div
        className="bg-white text-eatup-charcoal w-full sm:max-w-2xl rounded-t-3xl sm:rounded-3xl overflow-hidden fade-up max-h-[92vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b border-neutral-100 flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-eatup-charcoal text-eatup-yellow flex items-center justify-center">
            <ChefHat className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-eatup-green">
              Order #{order.order_id.slice(-6).toUpperCase()}
            </div>
            <div className="font-display text-xl font-bold truncate">{order.restaurant_name}</div>
            <div className="text-xs text-neutral-500 flex items-center gap-2 mt-0.5">
              <StatusBadge status={order.status} />
              <span>·</span>
              <span>{new Date(order.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
            </div>
          </div>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-eatup-gray hover:bg-neutral-200 flex items-center justify-center" data-testid="rest-close-details">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-5">
          {/* Verify driver identity — Uber Eats style */}
          {order.driver_id && !order.pickup_verified && !["delivered", "cancelled"].includes(order.status) && (
            <UberStyleVerifyCard
              driver={order.driver}
              onVerify={onVerifyPickup}
            />
          )}

          {order.pickup_verified && (
            <div className="rounded-3xl bg-eatup-green/10 border border-eatup-green/30 p-4 flex items-center gap-3" data-testid="driver-verified-badge">
              <div className="w-10 h-10 rounded-2xl bg-eatup-green text-white flex items-center justify-center">
                <Check className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold">Driver verified</div>
                <div className="text-xs text-neutral-500">Handoff completed successfully</div>
              </div>
            </div>
          )}

          {order.proof_of_delivery_image && (
            <div className="rounded-3xl overflow-hidden border border-eatup-green/40 bg-eatup-green/5" data-testid="rest-proof-image-card">
              <img src={order.proof_of_delivery_image} alt="Delivery proof" className="w-full aspect-video object-cover" />
              <div className="p-3 flex items-center gap-2 text-xs">
                <ClipboardCheck className="w-3.5 h-3.5 text-eatup-green" />
                <span className="font-semibold text-eatup-green">Delivery confirmed by driver photo</span>
              </div>
            </div>
          )}

          {order.pickup_photo_image && (
            <div className="rounded-3xl overflow-hidden border border-eatup-yellow/40 bg-eatup-yellow/10" data-testid="rest-pickup-image-card">
              <img src={order.pickup_photo_image} alt="Pickup photo" className="w-full aspect-video object-cover" />
              <div className="p-3 flex items-center gap-2 text-xs">
                <ClipboardCheck className="w-3.5 h-3.5 text-eatup-charcoal" />
                <span className="font-semibold text-eatup-charcoal">Pickup photo captured by driver</span>
              </div>
            </div>
          )}

          {/* Customer + Driver — both cards are fully clickable and open the contact modal */}
          <div className="grid sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setOpenContact("customer")}
              data-testid="rest-customer-card"
              className="border border-neutral-200 hover:border-eatup-green/40 hover:bg-eatup-green/[0.03] rounded-3xl p-4 text-left transition active:scale-[0.995]"
            >
              <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">Customer</div>
              <div className="mt-2 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-eatup-charcoal text-white flex items-center justify-center font-bold">
                  {(order.customer_name || "?")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{order.customer_name}</div>
                  <div className="text-xs text-neutral-500 truncate">{order.customer_phone}</div>
                </div>
                <div className="w-8 h-8 rounded-full bg-eatup-green/10 text-eatup-green flex items-center justify-center shrink-0">
                  <ChevronRight className="w-4 h-4" />
                </div>
              </div>
            </button>

            {order.driver_id ? (
              <button
                type="button"
                onClick={() => setOpenContact("driver")}
                data-testid="rest-driver-card"
                className="border border-neutral-200 hover:border-eatup-green/40 hover:bg-eatup-green/[0.03] rounded-3xl p-4 text-left transition active:scale-[0.995]"
              >
                <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">Driver</div>
                <div className="mt-2 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-eatup-green text-white flex items-center justify-center font-bold">
                    <Bike className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{order.driver?.name || "Assigned driver"}</div>
                    <div className="text-xs text-neutral-500 truncate">On the way</div>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-eatup-green/10 text-eatup-green flex items-center justify-center shrink-0">
                    <ChevronRight className="w-4 h-4" />
                  </div>
                </div>
              </button>
            ) : (
              <div className="border border-neutral-200 rounded-3xl p-4" data-testid="rest-driver-card-empty">
                <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">Driver</div>
                <div className="mt-2 text-sm text-neutral-500 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Waiting for driver to accept
                </div>
              </div>
            )}
          </div>

          {/* Delivery */}
          <div className="border border-neutral-200 rounded-3xl p-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mb-2">Deliver to</div>
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-eatup-green mt-1 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold">{order.address}</div>
                {order.instructions && (
                  <div className="mt-2 text-xs bg-eatup-yellow/20 text-amber-800 rounded-xl px-3 py-2 flex items-start gap-2">
                    <span className="font-bold">Note:</span>
                    <span>{order.instructions}</span>
                  </div>
                )}
              </div>
              <button onClick={copyAddress} className="text-xs font-semibold text-eatup-green px-3 py-1.5 rounded-full bg-eatup-green/10 flex items-center gap-1 shrink-0" data-testid="rest-copy-address">
                {copied ? <ClipboardCheck className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>

          {/* Items - kitchen ticket style */}
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mb-2 flex items-center justify-between">
              <span>Kitchen ticket</span>
              <button onClick={printTicket} data-testid="rest-print-btn" className="text-xs font-semibold text-eatup-charcoal bg-eatup-gray hover:bg-neutral-200 px-3 py-1.5 rounded-full flex items-center gap-1 normal-case tracking-normal">
                <Printer className="w-3 h-3" /> Print
              </button>
            </div>
            <div className="border-2 border-dashed border-neutral-300 rounded-3xl p-5 space-y-3 bg-white">
              {order.items.map((it) => (
                <div key={it.item_id} className="flex items-start gap-4" data-testid={`rest-detail-item-${it.item_id}`}>
                  <div className="w-12 h-12 rounded-lg bg-eatup-charcoal text-white flex items-center justify-center font-display font-bold text-lg tabular shrink-0">
                    {it.quantity}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold">{it.name}</div>
                    {it.notes && (
                      <div className="mt-1 text-xs bg-red-50 text-red-700 rounded-md px-2 py-1 inline-block font-semibold">
                        ⚠ {it.notes}
                      </div>
                    )}
                  </div>
                  <div className="text-sm font-semibold tabular text-neutral-500">${(it.price * it.quantity).toFixed(2)}</div>
                </div>
              ))}
              <div className="border-t border-dashed border-neutral-300 pt-3 mt-3 space-y-1 text-sm">
                <Row label="Subtotal" value={`$${order.subtotal.toFixed(2)}`} />
                {order.discount > 0 && <Row label={`Promo ${order.promo_code}`} value={`- $${order.discount.toFixed(2)}`} accent />}
                <Row label="Delivery" value={`$${order.delivery_fee.toFixed(2)}`} />
                <Row label="Service" value={`$${order.service_charge.toFixed(2)}`} />
                <Row label="Tax" value={`$${order.tax.toFixed(2)}`} />
                <Row label="Tip" value={`$${order.tip.toFixed(2)}`} />
                <div className="h-px bg-neutral-200 my-1" />
                <Row label="Customer total" value={`$${order.total.toFixed(2)}`} bold />
              </div>
              <div className="pt-2 flex items-center justify-between text-xs">
                <span className="text-neutral-500 capitalize">Payment: {order.payment_method.replace("_", " ")}</span>
                <span className={`font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${order.payment_status === "paid" ? "bg-eatup-green/10 text-eatup-green" : "bg-amber-100 text-amber-700"}`}>
                  {order.payment_status}
                </span>
              </div>
            </div>
          </div>

          {/* Timeline */}
          {timeline.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mb-2">Timeline</div>
              <div className="border border-neutral-200 rounded-3xl p-4 space-y-2">
                {timeline.map((t, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    <div className={`w-2 h-2 rounded-full ${i === timeline.length - 1 ? "bg-eatup-green" : "bg-neutral-300"}`} />
                    <div className="flex-1 font-medium capitalize">{t.status.replace("_", " ")}</div>
                    <div className="text-xs text-neutral-500">{new Date(t.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sticky footer */}
        <div className="border-t border-neutral-100 p-4 shrink-0 space-y-2">
          {canReportDelay && onReportDelay && (
            <div
              data-testid="rest-report-delay-strip"
              className="rounded-2xl bg-red-50 border border-red-200 px-4 py-3 flex items-center gap-3 flex-wrap"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Clock className="w-4 h-4 text-red-600 shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-black text-red-700">
                    Kitchen running late?
                  </div>
                  <div className="text-xs text-red-600 truncate">
                    {totalDelay > 0
                      ? `Already reported +${totalDelay} min · notify driver & customer of more?`
                      : "Bump ETA — driver & customer are notified instantly."}
                  </div>
                </div>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <button
                  data-testid="rest-report-delay-10"
                  onClick={() => onReportDelay(10)}
                  className="text-[11px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full bg-white border border-red-300 text-red-600 hover:bg-red-100 transition"
                >
                  + 10 min
                </button>
                <button
                  data-testid="rest-report-delay-20"
                  onClick={() => onReportDelay(20)}
                  className="text-[11px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full bg-red-600 text-white hover:bg-red-700 transition"
                >
                  + 20 min
                </button>
              </div>
            </div>
          )}
          {nextStatus && (
            <button
              onClick={() => onSetStatus(nextStatus)}
              data-testid="rest-advance-status-btn"
              className="w-full bg-eatup-green hover:bg-eatup-green-dark text-white rounded-full py-3.5 font-display font-bold text-base transition flex items-center justify-between px-5"
            >
              <span>Mark as {STATUS_LABEL[nextStatus]}</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
          <div className="grid grid-cols-2 gap-2">
            {canCancel ? (
              <button
                onClick={() => onSetStatus("cancelled")}
                data-testid="rest-cancel-btn"
                className="py-3 rounded-full text-red-600 hover:bg-red-50 font-semibold text-sm transition border border-red-100"
              >
                Cancel order
              </button>
            ) : (
              <button
                onClick={onClose}
                className="py-3 rounded-full bg-eatup-gray hover:bg-neutral-200 font-semibold text-sm transition"
              >
                Close
              </button>
            )}
            <button
              onClick={onClose}
              className={`py-3 rounded-full font-semibold text-sm transition ${canCancel ? "bg-eatup-gray hover:bg-neutral-200" : "bg-eatup-charcoal text-white hover:bg-black"}`}
              data-testid="rest-close-footer-btn"
            >
              {canCancel ? "Close" : "Done"}
            </button>
          </div>
        </div>
      </div>

      {openContact === "customer" && (
        <ContactDetailsModal
          role="customer"
          direction="restaurant_to_customer"
          order={order}
          contact={{
            name: order.customer_name,
            phone: order.customer_phone,
            address: order.address,
          }}
          onClose={() => setOpenContact(null)}
        />
      )}
      {openContact === "driver" && (
        <ContactDetailsModal
          role="driver"
          direction="restaurant_to_driver"
          order={order}
          contact={{
            name: order.driver?.name || "Assigned driver",
            phone: order.driver?.phone || order.driver_phone,
            photo: order.driver?.photo,
            vehicle: order.driver?.vehicle,
            vehicleDetail: order.driver?.vehicleDetail,
            rating: order.driver?.rating,
            subtitle: "En route to pickup",
          }}
          onClose={() => setOpenContact(null)}
        />
      )}
    </div>
  );
}

function Row({ label, value, accent, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`${bold ? "font-bold text-eatup-charcoal" : "text-neutral-500"}`}>{label}</span>
      <span className={`tabular ${accent ? "text-eatup-green font-semibold" : bold ? "font-bold" : "font-semibold"}`}>{value}</span>
    </div>
  );
}

function UberStyleVerifyCard({ driver, onVerify }) {
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [verifying, setVerifying] = useState(false);
  const [shake, setShake] = useState(false);
  const inputsRef = useRef([]);

  const focus = (idx) => {
    const el = inputsRef.current[idx];
    if (el) el.focus();
  };

  const setDigit = (idx, val) => {
    const v = (val || "").replace(/\D/g, "").slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[idx] = v;
      return next;
    });
    if (v && idx < 3) focus(idx + 1);
  };

  const onKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      focus(idx - 1);
    } else if (e.key === "ArrowLeft" && idx > 0) {
      focus(idx - 1);
    } else if (e.key === "ArrowRight" && idx < 3) {
      focus(idx + 1);
    } else if (e.key === "Enter") {
      handleVerify();
    }
  };

  const onPaste = (e) => {
    const text = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 4);
    if (!text) return;
    e.preventDefault();
    const arr = ["", "", "", ""];
    text.split("").forEach((c, i) => { arr[i] = c; });
    setDigits(arr);
    focus(Math.min(3, text.length));
  };

  const handleVerify = async () => {
    const code = digits.join("");
    if (code.length !== 4) return;
    setVerifying(true);
    try {
      await onVerify(code);
      setDigits(["", "", "", ""]);
    } catch {
      setShake(true);
      setTimeout(() => setShake(false), 400);
      setDigits(["", "", "", ""]);
      focus(0);
    } finally {
      setVerifying(false);
    }
  };

  const driverInitial = ((driver?.name || "D")[0] || "D").toUpperCase();

  return (
    <div className="rounded-3xl bg-white border border-neutral-200 shadow-lg overflow-hidden" data-testid="verify-driver-card">
      <div className="bg-eatup-charcoal text-white px-5 py-3 flex items-center gap-2">
        <Shield className="w-4 h-4 text-eatup-yellow" />
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-eatup-yellow">Confirm handoff</div>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-4">
          <div className="relative w-16 h-16 rounded-full bg-eatup-charcoal text-white flex items-center justify-center font-display font-extrabold text-2xl shrink-0 overflow-hidden">
            {driver?.avatar ? (
              <img src={driver.avatar} alt={driver?.name || "Driver"} className="w-full h-full object-cover" />
            ) : driverInitial}
            <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-eatup-green border-2 border-white flex items-center justify-center">
              <Bike className="w-3 h-3 text-white" />
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Delivery partner</div>
            <div className="font-display font-bold text-lg leading-tight truncate">{driver?.name || "Assigned driver"}</div>
            <div className="text-xs text-neutral-500 flex items-center gap-1">
              <span>⭐ 4.9</span><span>·</span><span>Honda scooter</span><span>·</span>
              <span className="text-eatup-green font-semibold">Arrived</span>
            </div>
          </div>
        </div>
        <div className="mt-5 text-sm text-eatup-charcoal">
          Ask the driver to read their <span className="font-semibold">4-digit pickup code</span> from the Eats GND Driver app.
        </div>
        <div className={`mt-4 flex items-center justify-center gap-2 sm:gap-3 ${shake ? "animate-[shake_0.4s_ease-in-out]" : ""}`}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputsRef.current[i] = el)}
              data-testid={`verify-digit-${i}`}
              value={d}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e)}
              onPaste={onPaste}
              onFocus={(e) => e.target.select()}
              inputMode="numeric"
              maxLength={1}
              className="w-14 h-16 sm:w-16 sm:h-20 rounded-2xl bg-eatup-gray border-2 border-transparent focus:bg-white focus:border-eatup-green focus:ring-4 focus:ring-eatup-green/10 text-center font-display font-extrabold text-3xl sm:text-4xl tabular outline-none transition"
            />
          ))}
        </div>
        <button
          data-testid="verify-submit-btn"
          disabled={digits.join("").length !== 4 || verifying}
          onClick={handleVerify}
          className="mt-5 w-full bg-eatup-charcoal hover:bg-black text-white rounded-full py-3.5 font-display font-bold text-base disabled:opacity-50 flex items-center justify-center gap-2 transition"
        >
          <Check className="w-4 h-4" /> {verifying ? "Verifying…" : "Confirm & release order"}
        </button>
        <div className="mt-3 text-[11px] text-neutral-500 text-center">
          Only release the food once the code matches. This protects the customer, your kitchen, and the driver.
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    placed: "bg-neutral-100 text-neutral-700",
    preparing: "bg-amber-100 text-amber-700",
    ready: "bg-blue-100 text-blue-700",
    picked_up: "bg-purple-100 text-purple-700",
    on_the_way: "bg-purple-100 text-purple-700",
    delivered: "bg-eatup-green/10 text-eatup-green",
    cancelled: "bg-red-100 text-red-700",
  };
  const label = STATUS_LABEL[status] || status;
  return (
    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${map[status] || "bg-neutral-100 text-neutral-700"}`}>
      {label}
    </span>
  );
}